// PR-AO — Player schedule "booked slot" info popup.
//
// Replaces the previous aggressive UX where clicking a booked slot in the
// player schedule grid auto-inserted the user into the waitlist with no
// confirmation. Now the click opens this dialog showing slot details +
// queue count, and the user must explicitly press "Ställ mig på kö" to
// commit.
//
// Used only by Book.tsx (player flow). Admin schedule has its own
// BookingDetailModal.

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { useTranslation } from "react-i18next";

interface BookingSlotInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courtName: string;
  startTime: Date;
  endTime: Date;
  /** Number of users currently in the waitlist (status='waiting') for this slot. */
  queueCount: number;
  /** Triggers the waitlist INSERT. Caller handles success/failure. */
  onConfirm: () => void;
  isPending: boolean;
}

export default function BookingSlotInfoDialog({
  open,
  onOpenChange,
  courtName,
  startTime,
  endTime,
  queueCount,
  onConfirm,
  isPending,
}: BookingSlotInfoDialogProps) {
  const { t } = useTranslation("booking");

  // Swedish-aware queue label: "Ingen i kö" / "1 person i kö" / "N personer i kö"
  const queueLabel =
    queueCount === 0
      ? t("slotInfo.queueNone", { defaultValue: "Ingen i kö ännu" })
      : queueCount === 1
        ? t("slotInfo.queueOne", { defaultValue: "1 person i kö" })
        : t("slotInfo.queueMany", { defaultValue: "{{count}} personer i kö", count: queueCount });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isPending) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("slotInfo.title", { defaultValue: "Bokad tid" })}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {/* Slot details */}
          <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5">
            <p className="font-medium text-sm">{courtName}</p>
            <p className="text-sm text-muted-foreground">
              {format(startTime, "EEE d MMM", { locale: sv })} · {format(startTime, "HH:mm")}–{format(endTime, "HH:mm")}
            </p>
            <Badge variant="secondary" className="mt-1">
              {t("slotInfo.statusBooked", { defaultValue: "Bokad" })}
            </Badge>
          </div>

          {/* Queue info */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4 shrink-0" />
            <span>{queueLabel}</span>
          </div>

          {/* Helper text */}
          <p className="text-xs text-muted-foreground leading-snug">
            {t("slotInfo.queueHelp", {
              defaultValue:
                "Om bokningen avbokas erbjuds tiden i kö-ordning. Du får en notis om en plats blir ledig.",
            })}
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t("slotInfo.cancel", { defaultValue: "Avbryt" })}
          </Button>
          <Button onClick={onConfirm} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("slotInfo.joining", { defaultValue: "Köar..." })}
              </>
            ) : (
              t("slotInfo.joinQueue", { defaultValue: "Ställ mig på kö" })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
